import React from 'react';

/**
 * Global Error Boundary — catches React render errors
 * and shows a recovery UI instead of a white screen.
 */
class ErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { hasError: false, error: null, errorInfo: null };
  }

  static getDerivedStateFromError(error) {
    return { hasError: true, error };
  }

  componentDidCatch(error, errorInfo) {
    this.setState({ errorInfo });
    // Log to console in development
    console.error('[ErrorBoundary] Caught error:', error, errorInfo);

    // Auto-reload on ChunkLoadError (stale deployment chunks)
    if (error?.name === 'ChunkLoadError' || error?.message?.includes('Loading chunk')) {
      const reloadKey = 'chunk-error-reload';
      const lastReload = sessionStorage.getItem(reloadKey);
      const now = Date.now();
      // Only auto-reload if we haven't done so in the last 10 seconds (prevent loops)
      if (!lastReload || now - Number(lastReload) > 10000) {
        sessionStorage.setItem(reloadKey, String(now));
        window.location.reload();
        return;
      }
    }
  }

  handleRetry = () => {
    this.setState({ hasError: false, error: null, errorInfo: null });
  };

  handleGoHome = () => {
    this.setState({ hasError: false, error: null, errorInfo: null });
    window.location.href = '/dashboard';
  };

  render() {
    if (this.state.hasError) {
      return (
        <div className="flex items-center justify-center min-h-[60vh] p-6">
          <div className="max-w-md w-full text-center">
            {/* Icon */}
            <div className="w-16 h-16 mx-auto mb-4 rounded-full bg-red-50 dark:bg-red-900/20 flex items-center justify-center">
              <span className="material-symbols-outlined text-red-500 text-3xl">
                error_outline
              </span>
            </div>

            {/* Title */}
            <h2 className="text-xl font-bold text-gray-800 dark:text-white mb-2">
              Algo salió mal
            </h2>

            {/* Description */}
            <p className="text-sm text-gray-500 dark:text-gray-400 mb-6">
              Ocurrió un error inesperado al cargar este módulo.
              Puedes intentar recargar la sección o volver al inicio.
            </p>

            {/* Error detail */}
            {this.state.error && (
              <details className="mb-6 p-3 rounded-lg bg-gray-50 dark:bg-gray-800 text-left border border-gray-200 dark:border-gray-700">
                <summary className="text-xs font-semibold text-gray-700 dark:text-gray-300 cursor-pointer">
                  Ver detalles técnicos del error
                </summary>
                <p className="mt-2 text-xs font-mono text-red-600 dark:text-red-400 break-all">
                  {this.state.error.toString()}
                </p>
                {this.state.errorInfo?.componentStack && (
                  <pre className="mt-2 text-[10px] font-mono text-gray-500 dark:text-gray-400 overflow-x-auto max-h-36">
                    {this.state.errorInfo.componentStack}
                  </pre>
                )}
              </details>
            )}

            {/* Actions */}
            <div className="flex gap-3 justify-center">
              <button
                onClick={this.handleRetry}
                className="flex items-center gap-2 px-5 py-2.5 rounded-lg bg-blue-600 hover:bg-blue-700 text-white text-sm font-medium transition-colors"
              >
                <span className="material-symbols-outlined text-[18px]">refresh</span>
                Reintentar
              </button>
              <button
                onClick={this.handleGoHome}
                className="flex items-center gap-2 px-5 py-2.5 rounded-lg bg-gray-100 hover:bg-gray-200 dark:bg-gray-700 dark:hover:bg-gray-600 text-gray-700 dark:text-gray-200 text-sm font-medium transition-colors"
              >
                <span className="material-symbols-outlined text-[18px]">home</span>
                Ir al Inicio
              </button>
            </div>
          </div>
        </div>
      );
    }

    return this.props.children;
  }
}

export default ErrorBoundary;
